import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  X,
  RefreshCw,
  ExternalLink,
  Smartphone,
  Monitor,
  Loader2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * DesignStudio — full-screen gallery design workspace.
 *
 * Opening "Live preview" no longer squeezes the admin page into a narrow
 * column next to a fixed panel. Instead the whole viewport becomes a focused
 * editor: a compact controls rail on the left (the Design tab, passed as
 * children) and the client-facing gallery preview filling everything else.
 *
 * Design changes are postMessage'd into the iframe (`lumetier-design-patch`)
 * so they render instantly without reloading the gallery.
 */
const LIMIT_KEY = "lumetier_studio_preview_n";
const LIMIT_OPTIONS = [24, 60, 0];

export default function DesignStudio({
  open,
  onClose,
  gallery,
  refreshKey,
  children,
}) {
  const iframeRef = useRef(null);
  const [device, setDevice] = useState("desktop");
  const [iframeKey, setIframeKey] = useState(0);
  const [ready, setReady] = useState(false);
  const [previewMeta, setPreviewMeta] = useState(null);
  // Once opened, the studio (and its iframe) stays mounted but hidden, so
  // re-entering is instant — no gallery reload, no image refetch.
  const [mounted, setMounted] = useState(false);
  const [limit, setLimit] = useState(() => {
    const v = parseInt(localStorage.getItem(LIMIT_KEY), 10);
    return LIMIT_OPTIONS.includes(v) ? v : 24;
  });

  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  // Gallery posts `lumetier-preview-ready` once its data has landed. Until
  // then a light overlay tells the studio user the preview is on its way —
  // the controls rail stays fully editable the whole time.
  useEffect(() => {
    if (!mounted) return undefined;
    setReady(false);
    const onMsg = (e) => {
      if (
        e.origin !== window.location.origin ||
        e.data?.type !== "lumetier-preview-ready"
      )
        return;
      setReady(true);
      setPreviewMeta({ total: e.data.total, shown: e.data.shown });
    };
    window.addEventListener("message", onMsg);
    const fallback = setTimeout(() => setReady(true), 20000);
    return () => {
      window.removeEventListener("message", onMsg);
      clearTimeout(fallback);
    };
  }, [mounted, iframeKey]);

  // Photos added/removed while the studio was closed → the background iframe
  // is stale, reload it on re-open. Design-only edits never trigger this.
  const photoSig = (gallery?.photos || []).map((p) => p.id).join(",");
  const lastSig = useRef(photoSig);
  useEffect(() => {
    if (!open || lastSig.current === photoSig) return;
    lastSig.current = photoSig;
    setIframeKey((k) => k + 1);
  }, [open, photoSig]);

  function changeLimit(v) {
    setLimit(v);
    try {
      localStorage.setItem(LIMIT_KEY, String(v));
    } catch {
      /* private mode */
    }
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "lumetier-preview-limit", limit: v },
        window.location.origin,
      );
    } catch {
      /* iframe not ready */
    }
  }

  // Pre-seed the gallery password so the embedded gallery unlocks itself.
  useEffect(() => {
    if (!open || !gallery?.slug) return;
    if (gallery.password) {
      try {
        sessionStorage.setItem(`gallery_pw_${gallery.slug}`, gallery.password);
      } catch (err) {
        console.warn(err);
      }
    }
  }, [open, gallery?.slug, gallery?.password]);

  // Escape closes the studio.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock the page scroll behind the overlay.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Instant design sync — push changed design fields into the iframe instead
  // of re-mounting it (a re-mount reloads every image and feels laggy).
  useEffect(() => {
    if (!mounted || !gallery) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    const patch = {};
    for (const k of [
      "template",
      "accent_color",
      "font_preset",
      "cover_layout",
      "cover_overlay_opacity",
      "cover_overlay_tone",
      "chapters",
      "cover_focal_x",
      "cover_focal_y",
      "signature_block",
      "welcome_message",
      "subtitle",
      "name",
      "cover_image",
    ]) {
      if (gallery[k] !== undefined) patch[k] = gallery[k];
    }
    try {
      win.postMessage(
        { type: "lumetier-design-patch", patch },
        window.location.origin,
      );
    } catch {
      /* iframe not ready */
    }
  }, [mounted, gallery, refreshKey, ready]);

  // `design=1` puts the gallery in a lightweight preview mode (capped photo
  // count, ≤1200px pre-generated variants) so it appears in seconds. The
  // count is part of the initial URL; later changes are postMessage'd.
  const previewUrl = gallery?.slug
    ? `${BACKEND}/gallery/${gallery.slug}?design=1&n=${limit || "all"}`
    : null;
  const fullUrl = gallery?.slug ? `${BACKEND}/gallery/${gallery.slug}` : null;

  if (!mounted) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className={`fixed inset-0 z-50 bg-background flex-col ${open ? "flex" : "hidden"}`}
      aria-hidden={!open}
      data-testid="live-preview-panel"
    >
      {/* ── Studio header ─────────────────────────────────────── */}
      <header className="shrink-0 flex items-center justify-between px-4 sm:px-6 h-14 border-b border-border bg-card">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
            data-testid="live-preview-close"
          >
            <X className="w-4 h-4" />{" "}
            <span className="hidden sm:inline">Exit studio</span>
          </button>
          <div className="w-px h-5 bg-border" />
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground hidden md:block">
            Design studio
          </div>
          <div className="text-sm font-medium truncate">{gallery?.name}</div>
          {previewMeta && previewMeta.total > previewMeta.shown && (
            <span
              className="hidden md:inline-flex items-center px-2 h-6 text-[10px] uppercase tracking-[0.18em] bg-muted text-muted-foreground whitespace-nowrap"
              title="The studio preview is capped so it loads fast. Open in a new tab to see every photo."
              data-testid="live-preview-photo-cap"
            >
              First {previewMeta.shown} of {previewMeta.total} photos
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <PhotoCountSelect
            limit={limit}
            total={previewMeta?.total}
            onChange={changeLimit}
          />
          <div className="w-px h-5 bg-border mx-1" />
          <DeviceToggle device={device} setDevice={setDevice} />
          <div className="w-px h-5 bg-border mx-1" />
          <button
            onClick={() => setIframeKey((k) => k + 1)}
            className="w-9 h-9 inline-flex items-center justify-center hover:bg-muted transition-colors"
            data-testid="live-preview-reload"
            aria-label="Reload preview"
            title="Reload"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <a
            href={fullUrl}
            target="_blank"
            rel="noreferrer"
            className="w-9 h-9 inline-flex items-center justify-center hover:bg-muted transition-colors"
            data-testid="live-preview-open"
            aria-label="Open in new tab"
            title="Open in new tab"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </header>

      {/* ── Split workspace ───────────────────────────────────── */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Controls rail */}
        <aside
          className="order-2 lg:order-1 shrink-0 w-full lg:w-[400px] xl:w-[440px] h-[45vh] lg:h-auto overflow-y-auto border-t lg:border-t-0 lg:border-r border-border bg-card"
          data-testid="design-studio-controls"
        >
          <div className="p-5">{open && children}</div>
        </aside>

        {/* Preview canvas */}
        <div className="order-1 lg:order-2 flex-1 bg-muted/40 overflow-hidden flex items-center justify-center p-3 lg:p-6">
          <div
            className="relative bg-background overflow-hidden shadow-xl border border-border/60 transition-all duration-300"
            style={
              device === "mobile"
                ? { width: 390, height: "100%", maxHeight: 844 }
                : { width: "100%", height: "100%" }
            }
          >
            {previewUrl ? (
              <>
                <iframe
                  key={iframeKey}
                  ref={iframeRef}
                  src={previewUrl}
                  title="Gallery preview"
                  className="w-full h-full border-0"
                  data-testid="live-preview-iframe"
                />
                {!ready && (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/70 backdrop-blur-sm pointer-events-none"
                    data-testid="live-preview-loading"
                  >
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                      Loading preview
                    </div>
                    <div className="text-xs text-muted-foreground/80 text-center px-6">
                      Keep editing — your changes apply the moment it's ready.
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
                Save the gallery to see a preview
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function PhotoCountSelect({ limit, total, onChange }) {
  return (
    <select
      value={limit}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-8 px-2 bg-muted/60 border-0 text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-border cursor-pointer"
      title="How many photos the studio preview renders — fewer loads faster"
      aria-label="Preview photo count"
      data-testid="live-preview-count"
    >
      <option value={24}>24 photos</option>
      <option value={60}>60 photos</option>
      <option value={0}>All{total ? ` (${total})` : ""}</option>
    </select>
  );
}

function DeviceToggle({ device, setDevice }) {
  return (
    <div className="inline-flex items-center bg-muted/60 p-0.5" role="tablist">
      <button
        onClick={() => setDevice("desktop")}
        className={`w-8 h-7 inline-flex items-center justify-center transition-colors ${device === "desktop" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        data-testid="live-preview-desktop"
        aria-label="Desktop frame"
        title="Desktop"
      >
        <Monitor className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => setDevice("mobile")}
        className={`w-8 h-7 inline-flex items-center justify-center transition-colors ${device === "mobile" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        data-testid="live-preview-mobile"
        aria-label="Mobile frame"
        title="Mobile"
      >
        <Smartphone className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
